<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
  breadcrumbNode,
} from "../../composables/useSeo";
import { usePhoneNumber } from "../../composables/usePhoneNumber";
import { useAnalytics } from "../../composables/useAnalytics";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { trackConversion } = useAnalytics();
const { servicePrice, serviceLabel, loadPrice } = usePricing();
const displayedServicePrice = servicePrice("garage-cleanout");
const displayedServiceLabel = serviceLabel("garage-cleanout");
const quarterTrailerPrice = loadPrice("quarter-trailer");
const halfTrailerPrice = loadPrice("half-trailer");
const fullTrailerPrice = loadPrice("full-trailer");

useSeoBase({
  title:
    "Garage Cleanout Services | Junk Guys MN - Complete Organization & Removal",
  description:
    "Professional garage cleanout services in the Twin Cities. Full organization, sorting assistance, and junk removal. Get your garage back today!",
  canonical: "https://junkguysmn.com/services/garage-cleanout",
  ogImage: "https://junkguysmn.com/og.jpg",
});

useJsonLd([
  orgNode(),
  localBusinessNode(),
  breadcrumbNode([
    { name: "Home", url: "https://junkguysmn.com" },
    { name: "Services", url: "https://junkguysmn.com/services" },
    {
      name: "Garage Cleanout",
      url: "https://junkguysmn.com/services/garage-cleanout",
    },
  ]),
  {
    "@type": "Service",
    name: "Garage Cleanout",
    description: "Complete garage organization and junk removal services",
    provider: {
      "@type": "Organization",
      name: "Junk Guys MN",
    },
    areaServed: {
      "@type": "Place",
      name: "Minneapolis-St. Paul Twin Cities Metro Area",
    },
    serviceType: "Garage Cleanout",
  },
]);
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">
            Garage Cleanout Services
          </h1>
          <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed">
            Complete garage organization and junk removal. We'll help you sort,
            organize, and reclaim your space.
          </p>
          <div class="mt-8 flex flex-wrap justify-center gap-4">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              📞 Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
            >
              📅 Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Overview -->
    <section class="py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl font-bold text-slate-900 mb-6">
              What We Handle
            </h2>
            <p class="text-lg text-slate-600 mb-6">
              Our garage cleanout service is comprehensive across Minneapolis,
              St. Paul, and the Twin Cities - we help you sort through
              everything, organize what you want to keep, and remove all the
              junk. We can also coordinate donations for usable items.
            </p>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Old Tools & Equipment</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Sports Equipment</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Storage Items</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Old Furniture</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Seasonal Items</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Construction Debris</span>
              </div>
            </div>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <h3 class="text-2xl font-bold text-slate-900 mb-6">
              Service Details
            </h3>
            <div class="space-y-4">
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700">{{
                  displayedServiceLabel
                }}</span>
                <span class="text-2xl font-bold text-primary-main">{{
                  displayedServicePrice
                }}</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Typical Duration</span
                >
                <span class="text-lg text-slate-600">2-6 hours</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Organization Help</span
                >
                <span class="text-green-600 font-semibold">Included</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Our Process -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Our Garage Cleanout Process
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            We make garage organization simple and stress-free
          </p>
        </div>
        <div class="grid md:grid-cols-4 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">1</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Assessment
            </h3>
            <p class="text-slate-600">
              We evaluate your garage space and discuss your goals for the
              cleanout.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">2</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">Sorting</h3>
            <p class="text-slate-600">
              We help you sort items into keep, donate, and remove categories.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">3</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Organization
            </h3>
            <p class="text-slate-600">
              We organize and arrange items you want to keep in a logical way.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">4</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">Cleanup</h3>
            <p class="text-slate-600">
              We remove all junk and leave your garage broom-clean and
              organized.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Garage Cleanout Pricing
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Transparent pricing based on garage size and amount of items
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">🚗</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">
              Single Car Garage
            </h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ quarterTrailerPrice }}
            </div>
            <p class="text-slate-600 mb-4">Light to moderate clutter</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Basic organization</li>
              <li>• Junk removal</li>
              <li>• 2-3 hours</li>
              <li>• Donation coordination</li>
            </ul>
          </div>
          <div
            class="bg-white rounded-2xl shadow-lg p-8 text-center border-2 border-primary-main"
          >
            <div class="text-4xl mb-4">🚗🚗</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">
              Double Car Garage
            </h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ halfTrailerPrice }}
            </div>
            <p class="text-slate-600 mb-4">Moderate to heavy clutter</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Full organization</li>
              <li>• Heavy junk removal</li>
              <li>• 4-5 hours</li>
              <li>• Storage solutions</li>
            </ul>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">🏠</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Large Garage</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ fullTrailerPrice }}
            </div>
            <p class="text-slate-600 mb-4">Heavy clutter or multiple areas</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Complete organization</li>
              <li>• Heavy equipment removal</li>
              <li>• 6+ hours</li>
              <li>• Custom solutions</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Factors -->
    <section class="bg-white py-16 border-t border-slate-200">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            What Affects Garage Cleanout Cost?
          </h2>
          <p class="text-lg text-slate-600">
            Our transparent pricing considers these key factors
          </p>
        </div>

        <div class="space-y-4">
          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">
                Garage Size & Volume
              </h3>
              <p class="text-slate-600">
                Single, double, or triple-car garage affects the scope. More
                items mean more truck space and labor time.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Type of Items</h3>
              <p class="text-slate-600">
                Heavy equipment, shelving units, and large tools require special
                handling compared to boxes and light debris.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Access & Layout</h3>
              <p class="text-slate-600">
                Narrow driveways, stairs, or cluttered pathways may require
                additional time and care during removal.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Organization Level</h3>
              <p class="text-slate-600">
                If you need sorting and organization help in addition to
                removal, this adds time to the project scope.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Same-Day Service</h3>
              <p class="text-slate-600">
                Need it done urgently? We offer same-day garage cleanout when
                our schedule allows.
              </p>
            </div>
          </div>
        </div>

        <div
          class="mt-10 p-6 bg-primary-main/10 border-l-4 border-primary-main rounded-r-lg"
        >
          <p class="text-lg text-slate-900">
            <strong>Ready for an accurate quote?</strong> Call
            <a
              :href="phoneNumberTel"
              class="font-bold text-primary-main hover:text-primary-dark underline"
              >(612) 424-0609</a
            >
            for a fast estimate. We provide upfront pricing before we start-no
            surprises.
          </p>
        </div>
      </div>
    </section>

    <!-- Why Choose Junk Guys MN -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Why Choose Junk Guys MN
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional garage cleanout service you can trust
          </p>
        </div>
        <div class="grid md:grid-cols-4 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🏠</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Locally Owned
            </h3>
            <p class="text-slate-600">
              Family-owned business serving the Twin Cities communities
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">⚡</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Same-Day Service
            </h3>
            <p class="text-slate-600">
              Quick response with same-day availability when you need it
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">♻️</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Eco-Friendly
            </h3>
            <p class="text-slate-600">
              We donate and recycle whenever possible to reduce landfill waste
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">💰</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Upfront Pricing
            </h3>
            <p class="text-slate-600">
              Transparent quotes with no hidden fees or surprises
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Areas -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">Service Areas</h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Providing garage cleanout services throughout Minneapolis, St. Paul,
            and surrounding Twin Cities communities
          </p>
        </div>
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/minneapolis/garage-cleanout"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Minneapolis</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/saint-paul/garage-cleanout"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Saint Paul</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/bloomington"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Bloomington</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/eden-prairie"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Eden Prairie</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/maple-grove"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Maple Grove</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/brooklyn-park"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Brooklyn Park</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/lakeville"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Lakeville</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/services"
              class="text-slate-700 hover:text-primary-main font-semibold"
              >+ More Areas</NuxtLink
            >
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p class="text-lg text-slate-600">
            Common questions about garage cleanout services
          </p>
        </div>
        <div
          class="bg-white rounded-2xl shadow-sm border border-slate-200 divide-y divide-slate-200"
        >
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do you help organize items we want to keep?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Absolutely! We don't just remove junk - we help you organize and
                arrange the items you want to keep. We can suggest storage
                solutions and create a logical organization system for your
                garage.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  What happens to items that are still usable?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We coordinate with local charities and donation centers to give
                usable items a second life. This includes furniture, tools,
                sports equipment, and other items in good condition.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How long does a garage cleanout take?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Most garage cleanouts take 2-6 hours depending on the size and
                amount of clutter. We work efficiently and can often complete
                the job in a single visit.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How much does garage cleanout cost?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Pricing varies based on garage size and volume. Garage cleanouts
                use our load-based pricing, starting at
                {{ displayedServicePrice }}. Call
                <a
                  :href="phoneNumberTel"
                  class="font-semibold text-primary-main hover:text-primary-dark underline"
                  >(612) 424-0609</a
                >
                for an accurate quote based on your specific needs.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do you offer same-day garage cleanout?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Yes! We often have same-day availability throughout Minneapolis,
                St. Paul, and surrounding Twin Cities suburbs. Contact us early
                in the day for the best chance at same-day service.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do I need to be present during the cleanout?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                While we recommend being present to point out what stays and
                what goes, it's not required. Many customers arrange for us to
                work while they're away-just clearly mark items to keep and
                arrange payment in advance.
              </div>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-7xl px-4 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Ready to Reclaim Your Garage?
        </h2>
        <p class="text-xl text-white mb-8 max-w-2xl mx-auto">
          Get your garage organized and clutter-free today. Professional
          organization and junk removal included.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call {{ phoneNumberPretty }}
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
